import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { HandCoins } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"
import { SelectorBuscable } from "@/componentes/ui/SelectorBuscable"
import { paraElServidor } from "@/lib/decimales"
import { Campo } from "@/paginas/interno/Gastos"

type Persona = { id: number; nombres: string; apellidos: string }
type Proveedor = { id: number; nombre: string }
type Divisa = { id: number; codigo: string; nombre: string }
type Creada = { orden_id: number; codigo: string; monto: string }

/** Hoy en la zona del navegador, en `AAAA-MM-DD`. */
function hoy(): string {
  const d = new Date()
  const dos = (n: number) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${dos(d.getMonth() + 1)}-${dos(d.getDate())}`
}

/**
 * Un pago de sueldo que no sale de una corrida: un bono, un anticipo, una terapia.
 *
 * ⚠ **Existe para que un sueldo no se redacte nunca desde «Órdenes de pago».** Hay
 * pagos a la gente de la casa que no caben en un período —son puntuales, de monto
 * distinto cada vez, o de alguien sin contrato vivo—, y el único camino era teclear
 * una orden a mano, donde el renglón salía como «Servicio» y el pago quedaba fuera de
 * la cuenta de lo que se le paga a cada quien.
 *
 * ⚠ **No crea recibo ni período.** Un recibo explica un cálculo; aquí hay un importe
 * que alguien decidió. La orden nace en BORRADOR, como todo lo que redacta la nómina.
 */
export function PagoPuntual({ onCreada }: { onCreada?: (ordenId: number) => void }) {
  const clienteQuery = useQueryClient()
  const [abierto, setAbierto] = useState(false)
  const [personaId, setPersonaId] = useState<number | null>(null)
  const [proveedorId, setProveedorId] = useState<number | null>(null)
  const [divisaId, setDivisaId] = useState<number | null>(null)
  const [hecha, setHecha] = useState<Creada | null>(null)

  const personas = useQuery({
    queryKey: ["personas", "empleados"],
    // ⚠ `/personas` devuelve una PÁGINA (`{items, total}`), no una lista: leerla como
    // lista tumbaba la pantalla con «.map is not a function» (10/10).
    queryFn: async () =>
      (await api.get<{ items: Persona[] }>("/api/v1/personas?limit=100&personal=true")).items,
    enabled: abierto,
  })
  const proveedores = useQuery({
    queryKey: ["proveedores", "ADMINISTRATIVO"],
    queryFn: () =>
      api.get<Proveedor[]>("/api/v1/proveedores?limit=100&activo=true&categoria=ADMINISTRATIVO"),
    enabled: abierto,
  })
  const divisas = useQuery({
    queryKey: ["divisas"],
    queryFn: () => api.get<Divisa[]>("/api/v1/divisas"),
    enabled: abierto,
  })

  const crear = useMutation({
    mutationFn: (cuerpo: Record<string, unknown>) =>
      api.post<Creada>("/api/v1/nomina/pagos-puntuales", cuerpo),
    onSuccess: (c) => {
      clienteQuery.invalidateQueries({ queryKey: ["ordenes-pago"] })
      setHecha(c)
      onCreada?.(c.orden_id)
    },
  })

  function cerrar() {
    setAbierto(false)
    setHecha(null)
    crear.reset()
  }

  return (
    <>
      <Boton variante="secundario" onClick={() => setAbierto(true)}>
        <HandCoins size={16} /> Pago puntual
      </Boton>

      <Modal
        abierto={abierto}
        titulo="Pago puntual de sueldo"
        descripcion="Un bono, un anticipo o un pago que no sale de una corrida. Nace en borrador."
        onCerrar={cerrar}
      >
        {hecha ? (
          <div className="space-y-4">
            <p className="text-sm">
              Redactada <strong>{hecha.codigo}</strong> por {hecha.monto}. Está en borrador:
              revísala y envíala a aprobación como cualquier otra.
            </p>
            <div className="flex justify-end">
              <Boton variante="primario" onClick={cerrar}>
                Listo
              </Boton>
            </div>
          </div>
        ) : (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault()
              const datos = new FormData(e.currentTarget)
              const texto = (c: string) => String(datos.get(c) ?? "").trim()
              if (personaId === null || proveedorId === null || divisaId === null) return
              crear.mutate({
                persona_id: personaId,
                proveedor_id: proveedorId,
                divisa_id: divisaId,
                concepto: texto("concepto"),
                monto: paraElServidor(texto("monto")),
                fecha: texto("fecha"),
                tipo_anticipo: texto("tipo_anticipo") || null,
              })
            }}
          >
            <Campo para="pp-persona" etiqueta="Empleado">
              <SelectorBuscable
                id="pp-persona"
                opciones={(personas.data ?? []).map((p) => ({
                  valor: p.id,
                  etiqueta: `${p.nombres} ${p.apellidos}`,
                }))}
                valor={personaId}
                onCambiar={setPersonaId}
              />
            </Campo>

            <Campo
              para="pp-proveedor"
              etiqueta="Se le paga a"
              ayuda="Su ficha administrativa: es a quien la orden le transfiere."
            >
              <SelectorBuscable
                id="pp-proveedor"
                opciones={(proveedores.data ?? []).map((p) => ({
                  valor: p.id,
                  etiqueta: p.nombre,
                }))}
                valor={proveedorId}
                onCambiar={setProveedorId}
              />
            </Campo>

            <Campo para="pp-concepto" etiqueta="Concepto" ayuda="De qué es el pago.">
              <input id="pp-concepto" name="concepto" required className="campo mt-1" />
            </Campo>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo para="pp-monto" etiqueta="Monto">
                <input
                  id="pp-monto"
                  name="monto"
                  inputMode="decimal"
                  required
                  className="campo mt-1 tabular-nums"
                />
              </Campo>
              <Campo para="pp-divisa" etiqueta="Moneda">
                <SelectorBuscable
                  id="pp-divisa"
                  opciones={(divisas.data ?? []).map((d) => ({
                    valor: d.id,
                    etiqueta: `${d.codigo} — ${d.nombre}`,
                  }))}
                  valor={divisaId}
                  onCambiar={setDivisaId}
                />
              </Campo>
            </div>

            <Campo
              para="pp-fecha"
              etiqueta="Fecha"
              ayuda="La del extracto si ya se pagó, para que cuadre con el banco."
            >
              <input
                id="pp-fecha"
                name="fecha"
                type="date"
                required
                defaultValue={hoy()}
                className="campo mt-1"
              />
            </Campo>

            {/* Un anticipo o un préstamo entra en la cuenta del empleado al pagarse, y lo
                abonan los ingresos que se reporten con él en Tesorería → «Ingresos». */}
            <Campo
              para="pp-anticipo"
              etiqueta="¿Lo tiene que devolver?"
              ayuda="Un anticipo o un préstamo queda en su cuenta hasta que lo devuelva."
            >
              <select id="pp-anticipo" name="tipo_anticipo" defaultValue="" className="campo mt-1">
                <option value="">No: es un pago (bono, terapia…)</option>
                <option value="ANTICIPO">Sí, es un anticipo</option>
                <option value="PRESTAMO">Sí, es un préstamo</option>
              </select>
            </Campo>

            {crear.error instanceof ErrorApi && (
              <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
                {crear.error.message}
              </p>
            )}

            <div className="flex justify-end gap-2">
              <Boton variante="fantasma" onClick={cerrar}>
                Cancelar
              </Boton>
              <Boton
                type="submit"
                variante="primario"
                disabled={
                  crear.isPending || personaId === null || proveedorId === null || divisaId === null
                }
              >
                Redactar la orden
              </Boton>
            </div>
          </form>
        )}
      </Modal>
    </>
  )
}
